import {Box, Stack, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAnswerContext, surveyQuestionTitle} from "../shared/surveyAnswer.util";
import SurveyQuestionMarkers from "../shared/SurveyQuestionMarkers";
import {getSurveyQuestionTypeIcon, getSurveyQuestionTypeLabelKey} from "../shared/surveyQuestionTypes.util";

/**
 * One typography for a submission's answers, and the question heading that sits above them.
 *
 * WHY it is shared rather than restated in each view: the blackout switch swaps the read-only
 * answers for the editor IN PLACE. Any difference in size or leading between the two therefore
 * reads as the whole column re-typesetting itself when the switch is flipped — so both views take
 * their metrics from here, and every answer type gets the same ones whether it is guest-typed text,
 * a score, a date or a list of chosen options.
 *
 * The Required and Sensitive flags are the one thing this file does NOT draw itself: they are the
 * shared markers, sized from the title they sit in, so a question cannot read one way here and
 * another way in the list or the export.
 */

export const SURVEY_ANSWER_FONT_SIZE = 16;

export const SURVEY_ANSWER_LINE_HEIGHT = 1.55;

const QUESTION_TITLE_FONT_SIZE = 15;

const QUESTION_TITLE_LINE_HEIGHT = 1.45;

const QUESTION_TYPE_ICON_SIZE = 16;

/** The answer's own box. Every answer type renders at this size, text or not. */
export const surveyAnswerValueSx = {
    fontSize: SURVEY_ANSWER_FONT_SIZE,
    lineHeight: SURVEY_ANSWER_LINE_HEIGHT,
    overflowWrap: 'anywhere',
} as const;

export const SURVEY_ANSWER_BLOCK_GAP = 0.75;

export const SurveyQuestionTitleRow = ({question, ctx}: {
    question: SurveyQuestion
    ctx: SurveyAnswerContext
}) => (
    <Stack direction={'row'} alignItems={'flex-start'} gap={0.75} sx={{minWidth: 0}}>
        <Tooltip title={ctx.t(getSurveyQuestionTypeLabelKey(question.type))}>
            <Box
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    height: QUESTION_TITLE_FONT_SIZE * QUESTION_TITLE_LINE_HEIGHT,
                }}
            >
                {getSurveyQuestionTypeIcon(question.type, QUESTION_TYPE_ICON_SIZE)}
            </Box>
        </Tooltip>

        <Typography
            variant={'body1'}
            color={'text.secondary'}
            sx={{
                fontSize: QUESTION_TITLE_FONT_SIZE,
                fontWeight: 500,
                lineHeight: QUESTION_TITLE_LINE_HEIGHT,
                minWidth: 0,
            }}
        >
            {surveyQuestionTitle(question, ctx)}

            <SurveyQuestionMarkers question={question} t={ctx.t}/>
        </Typography>
    </Stack>
);

/** The placeholder for a question the guest skipped — same metrics, muted. */
export const SurveyAnswerNotAnswered = ({label}: {label: string}) => (
    <Typography component={'span'} color={'text.disabled'} sx={{fontSize: 'inherit'}}>
        {label}
    </Typography>
);
